import { differenceInDays } from 'date-fns';
import {
  COMPONENT_ACCOMMODATION,
  COMPONENT_FLIGHT,
  COMPONENT_TOUR,
  COMPONENT_CRUISE,
  COMPONENT_COACH,
  COMPONENT_CAR_HIRE,
  COMPONENT_TRANSFER,
  COMPONENT_PACKAGE,
  COMPONENT_EXTRA,
} from 'shared/constants/common';
import {
  getStringValue,
  displayDate,
  getPluralForm,
  removeEmptyFromList,
} from './common';

const DEFAULT_START_DATE_LABEL = 'Start Date';
const DEFAULT_END_DATE_LABEL = 'End Date';

export const getAirportDisplay = (name = '', code = '') => {
  const airportCode = code ? `(${code})` : '';
  return `${name} ${airportCode}`.trim();
};

export const getAllFlightSegments = (flights = []) => {
  const segments = [];

  flights.forEach((flight) => {
    if (flight?.legs?.length < 1) {
      return;
    }

    flight.legs.forEach((leg) => {
      if (leg?.segments?.length < 1) {
        return;
      }

      leg.segments.forEach((segment) => {
        segments.push(segment);
      });
    });
  });

  return segments;
};

const getDefaultOverviewDetails = ({ component, outbound, inbound } = {}) => {
  const { startDate, endDate } = component;
  const now = new Date();

  let statusLabel = '';
  if (inbound && new Date(inbound) < now) {
    statusLabel = 'Returned';
  } else if (outbound && new Date(outbound) < now) {
    statusLabel = 'Departed';
  }

  return {
    startDateDiffToNow: outbound
      ? differenceInDays(new Date(outbound), now)
      : '',
    startDate: displayDate(startDate?.timestamp, ' '),
    startDateLabel: DEFAULT_START_DATE_LABEL,
    endDate: displayDate(endDate?.timestamp, ' '),
    endDateLabel: DEFAULT_END_DATE_LABEL,
    dateToDisplay: `${displayDate(outbound, ' ')} - ${displayDate(inbound, ' ')}`,
    statusLabel,
  };
};

const getAccommodationOverview = ({ component, outbound, inbound } = {}) => {
  if (!component) {
    return {};
  }

  const { name } = component;

  return {
    name: getStringValue(name),
    ...getDefaultOverviewDetails({ component, outbound, inbound }),
    startDateLabel: 'Check in',
    endDateLabel: 'Check out',
  };
};

const getFlightOverview = ({ component, outbound, inbound } = {}) => {
  if (!component) {
    return {};
  }

  const { departureDate, arrivalDate, departureAirport, arrivalAirport } =
    component;

  const departureAirportLabel = getAirportDisplay(
    departureAirport?.name,
    departureAirport.code,
  );
  const arrivalAirportLabel = getAirportDisplay(
    arrivalAirport?.name,
    arrivalAirport.code,
  );

  return {
    name: `${departureAirportLabel} - ${arrivalAirportLabel}`,
    startDateDiffToNow: outbound
      ? differenceInDays(new Date(outbound), new Date())
      : '',
    startDate: displayDate(departureDate, ' '),
    startDateLabel: 'Departure Date',
    endDate: displayDate(arrivalDate, ' '),
    endDateLabel: 'Arrival Date',
    departureAirport,
    arrivalAirport,
    dateToDisplay: `${displayDate(outbound, ' ')} - ${displayDate(
      inbound,
      ' ',
    )}`,
  };
};

const getTourOverview = ({ component, outbound, inbound } = {}) => {
  if (!component) {
    return {};
  }

  const { tourName } = component;

  return {
    name: tourName,
    ...getDefaultOverviewDetails({ component, outbound, inbound }),
  };
};

const getCruiseOverview = ({ component, outbound, inbound } = {}) => {
  if (!component) {
    return {};
  }

  const { itineraryName, shipName } = component;

  return {
    name: itineraryName || shipName,
    ...getDefaultOverviewDetails({ component, outbound, inbound }),
  };
};

const getCoachOrExtraOverview = ({ component, outbound, inbound } = {}) => {
  if (!component) {
    return {};
  }

  const { name } = component;

  return {
    name,
    ...getDefaultOverviewDetails({ component, outbound, inbound }),
  };
};

const getCarHireOverview = ({ component, outbound, inbound } = {}) => {
  if (!component) {
    return {};
  }

  const { vehicleDescription } = component;

  return {
    name: vehicleDescription,
    ...getDefaultOverviewDetails({ component, outbound, inbound }),
    startDateLabel: 'Pick up Date',
    endDateLabel: 'Drop off Date',
  };
};

const getTransferOverview = ({ component, outbound, inbound } = {}) => {
  if (!component) {
    return {};
  }

  const { vehicle } = component;

  return {
    name: vehicle,
    ...getDefaultOverviewDetails({ component, outbound, inbound }),
  };
};

const getPackageOverview = ({ component, outbound, inbound } = {}) => {
  if (!component) {
    return {};
  }

  const { holidayNumber } = component;

  return {
    name: holidayNumber,
    ...getDefaultOverviewDetails({ component, outbound, inbound }),
  };
};

export const getQuoteBookingComponentOverview = (item) => {
  const {
    accommodation,
    extras,
    flights,
    tours,
    carHires,
    transfers,
    coaches,
    cruises,
    packages,
    outbound,
    inbound,
  } = item;

  if (!item) {
    return {};
  }

  if (cruises?.length >= 1 && (cruises[0].itineraryName || cruises[0].shipName)) {
    const firstCruise = cruises[0];

    return {
      componentType: COMPONENT_CRUISE,
      ...getCruiseOverview({ component: firstCruise, outbound, inbound }),
    };
  }

  if (accommodation?.length >= 1) {
    const firstAccommodation = accommodation[0];

    return {
      componentType: COMPONENT_ACCOMMODATION,
      ...getAccommodationOverview({
        component: firstAccommodation,
        outbound,
        inbound,
      }),
    };
  }

  if (flights?.[0]?.legs?.[0]?.segments?.[0]) {
    const flightSegments = getAllFlightSegments(flights);
    const firstFlight = flightSegments[0];

    return {
      componentType: COMPONENT_FLIGHT,
      ...getFlightOverview({ component: firstFlight, outbound, inbound }),
    };
  }

  if (tours?.length >= 1) {
    const firstTour = tours[0];

    return {
      componentType: COMPONENT_TOUR,
      ...getTourOverview({ component: firstTour, outbound, inbound }),
    };
  }

  if (coaches?.length >= 1) {
    const firstCoach = coaches[0];

    return {
      componentType: COMPONENT_COACH,
      ...getCoachOrExtraOverview({ component: firstCoach, outbound, inbound }),
    };
  }

  if (carHires?.length >= 1) {
    const firstCarHire = carHires[0];

    return {
      componentType: COMPONENT_CAR_HIRE,
      ...getCarHireOverview({
        component: firstCarHire,
        outbound,
        inbound,
      }),
    };
  }

  if (transfers?.length >= 1) {
    const firstTransfer = transfers[0];

    return {
      componentType: COMPONENT_TRANSFER,
      ...getTransferOverview({ component: firstTransfer, outbound, inbound }),
    };
  }

  if (packages?.length >= 1) {
    const firstPackage = packages[0];

    return {
      componentType: COMPONENT_PACKAGE,
      ...getPackageOverview({ component: firstPackage, outbound, inbound }),
    };
  }

  if (extras?.length >= 1) {
    const firstExtra = extras[0];

    return {
      componentType: COMPONENT_EXTRA,
      ...getCoachOrExtraOverview({
        component: firstExtra,
        outbound,
        inbound,
      }),
    };
  }

  return {};
};

export const getPassengersToDisplay = (passengerSummary = {}) => {
  const passengerLabels = [];
  const { adults, children, infants } = passengerSummary;

  if (adults > 0) {
    passengerLabels.push(`${getPluralForm('Adult', adults, true)}`);
  }

  if (children > 0) {
    passengerLabels.push(
      children === 1 ? `${children} Child` : `${children} Children`
    );
  }

  if (infants > 0) {
    passengerLabels.push(`${getPluralForm('Infant', infants, true)}`);
  }

  return removeEmptyFromList(passengerLabels).join(', ');
};

export const generateTitle = (
  outbound,
  destination,
  destinations,
  accommodation,
  cruises,
  tours,
  flights,
  isLoyaltyBooking,
  isHorizontal,
) => {
  if (!outbound) return 'Your trip';
  if (isLoyaltyBooking && isHorizontal) return `Bundle: ${tours[0]?.tourName}`;

  const formattedOutbound = displayDate(outbound);

  const getDestination = (prefix, value) => {
    if (Array.isArray(value) && value.length <= 0) return null;
    return value ? `Departing ${formattedOutbound} ${prefix} ${value}` : null;
  };

  const destinationValue =
    destination && destination !== '' ? destination : null;

  const titleOptions = [
    getDestination('to', destinationValue || destinations[0]),
    getDestination('to', accommodation[0]?.resort),
    getDestination('on', cruises[0]?.shipName),
    getDestination('on', tours[0]?.tourName),
    getDestination(
      'to',
      flights[0]?.legs?.[0]?.segments?.[0]?.arrivalAirport?.name,
    ),
  ];

  return (
    titleOptions.find(Boolean) || `Your trip departing on ${formattedOutbound}`
  );
};
